"use client";

import { useTranslations } from "next-intl";
import type * as React from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { XIcon } from "@/components/ui/XIcon";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

interface ResponsiveModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  children: React.ReactNode;
  desktopMaxWidth?: "sm" | "md" | "lg";
  compact?: boolean;
  /**
   * Also render an explicit close button on the mobile drawer. Off by default:
   * a bottom sheet is already dismissible by overlay tap / swipe / Esc, and the
   * existing consumers rely on that. Opt in where the design calls for the X.
   */
  showMobileClose?: boolean;
}

export const ResponsiveModal: React.FC<ResponsiveModalProps> = ({
  open,
  onOpenChange,
  title,
  children,
  desktopMaxWidth = "sm",
  compact = false,
  showMobileClose = false,
}) => {
  const t = useTranslations("stake");
  const isMobile = useMediaQuery("(max-width: 767px)");

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} direction="bottom">
        <DrawerContent
          aria-describedby={undefined}
          className={cn(
            "bg-bg-secondary rounded-t-lg pb-8 px-6",
            compact && "px-[16px] pb-[20px]",
          )}
        >
          <DrawerHeader
            className={cn(
              "flex items-center justify-between px-0 pt-6 pb-4",
              compact && "pt-[16px] pb-[12px]",
            )}
          >
            <DrawerTitle
              className={cn(
                "text-base font-bold text-text-primary",
                compact && "text-[15px]",
              )}
            >
              {title}
            </DrawerTitle>
          </DrawerHeader>
          {children}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className={cn(
          "bg-bg-secondary pt-6 pb-8 px-8",
          compact && "px-[20px] pt-[18px] pb-[20px]",
          {
            "max-w-sm": desktopMaxWidth === "sm",
            "max-w-md": desktopMaxWidth === "md",
            "max-w-lg": desktopMaxWidth === "lg",
          },
        )}
      >
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle
            className={cn(
              "text-base font-bold text-text-primary",
              compact && "text-[15px]",
            )}
          >
            {title}
          </DialogTitle>
          <DialogClose
            aria-label={t("modalCommon.close")}
            className="text-text-primary transition-opacity hover:opacity-70"
          >
            <XIcon size={24} />
          </DialogClose>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
};
